import React, { useState, useEffect, useCallback } from 'react';
import { analyticsAPI } from '../../lib/api';
import {
  Users, Globe, Monitor, Smartphone, Tablet, RefreshCw,
  UserCheck, UserX, ArrowUpRight, ArrowDownRight, Clock,
  ChevronLeft, ChevronRight, Filter, Search
} from 'lucide-react';

function StatCard({ label, value, sub, icon: Icon, color, testId }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-5 hover:shadow-md transition-shadow" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-black text-zinc-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-zinc-400 mt-1">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

function OverviewSection({ overview, loading }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-pulse">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-zinc-200 p-5">
            <div className="h-3 bg-zinc-100 rounded w-2/3 mb-3" />
            <div className="h-7 bg-zinc-100 rounded w-1/3" />
          </div>
        ))}
      </div>
    );
  }
  if (!overview) return null;

  const cards = [
    { label: 'Total Users', value: overview.total_users, sub: 'Registered accounts', icon: Users, color: 'bg-blue-50 text-blue-600', testId: 'stat-total-users' },
    { label: 'Online Now', value: overview.online_now, sub: 'Active in last 5 min', icon: UserCheck, color: 'bg-emerald-50 text-emerald-600', testId: 'stat-online-now' },
    { label: 'Male Online', value: overview.male_online, icon: ArrowUpRight, color: 'bg-sky-50 text-sky-600', testId: 'stat-male-online' },
    { label: 'Female Online', value: overview.female_online, icon: ArrowDownRight, color: 'bg-pink-50 text-pink-600', testId: 'stat-female-online' },
    { label: 'Other Online', value: overview.other_online, icon: Users, color: 'bg-violet-50 text-violet-600', testId: 'stat-other-online' },
    { label: 'New Today', value: overview.new_today, sub: 'Registered today', icon: ArrowUpRight, color: 'bg-amber-50 text-amber-600', testId: 'stat-new-today' },
    { label: 'New This Week', value: overview.new_week, sub: 'Last 7 days', icon: Clock, color: 'bg-orange-50 text-orange-600', testId: 'stat-new-week' },
    { label: 'New This Month', value: overview.new_month, sub: 'Last 30 days', icon: Clock, color: 'bg-teal-50 text-teal-600', testId: 'stat-new-month' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-overview-grid">
      {cards.map(c => <StatCard key={c.testId} {...c} />)}
    </div>
  );
}

function ActivityTable({ activities, total, page, pages, onPageChange, loading, filters, onFilterChange }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden" data-testid="activity-table-section">
      <div className="p-4 border-b border-zinc-100">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-zinc-900 flex items-center gap-2">
            <Users className="w-4 h-4 text-[#002FA7]" /> User Activity
            <span className="text-xs font-normal text-zinc-400 ml-1">({total} total)</span>
          </h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={filters.status || ''}
            onChange={e => onFilterChange({ ...filters, status: e.target.value || undefined })}
            className="text-xs border border-zinc-200 rounded-lg px-3 py-1.5 bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
            data-testid="activity-filter-status"
          >
            <option value="">All Status</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
          </select>
          <select
            value={filters.device || ''}
            onChange={e => onFilterChange({ ...filters, device: e.target.value || undefined })}
            className="text-xs border border-zinc-200 rounded-lg px-3 py-1.5 bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
            data-testid="activity-filter-device"
          >
            <option value="">All Devices</option>
            <option value="Desktop">Desktop</option>
            <option value="Mobile">Mobile</option>
            <option value="Tablet">Tablet</option>
          </select>
          <input
            type="text"
            placeholder="Filter by country..."
            value={filters.country || ''}
            onChange={e => onFilterChange({ ...filters, country: e.target.value || undefined })}
            className="text-xs border border-zinc-200 rounded-lg px-3 py-1.5 bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-[#002FA7] w-36"
            data-testid="activity-filter-country"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="activity-table">
          <thead>
            <tr className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wide">
              <th className="text-left px-4 py-3 font-medium">User</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Country</th>
              <th className="text-left px-4 py-3 font-medium">Device</th>
              <th className="text-left px-4 py-3 font-medium">OS</th>
              <th className="text-left px-4 py-3 font-medium">Browser</th>
              <th className="text-left px-4 py-3 font-medium">Last Active</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i} className="border-t border-zinc-100 animate-pulse">
                  {[...Array(7)].map((__, j) => (
                    <td key={j} className="px-4 py-3"><div className="h-4 bg-zinc-100 rounded w-20" /></td>
                  ))}
                </tr>
              ))
            ) : activities.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-400 text-sm">No activity data yet. Users will appear after their first heartbeat.</td></tr>
            ) : (
              activities.map((a, i) => (
                <tr key={a.user_id || i} className="border-t border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`activity-row-${i}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-zinc-900 text-sm">{a.name || 'Unknown'}</p>
                    <p className="text-xs text-zinc-400">{a.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${a.is_online ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${a.is_online ? 'bg-emerald-500' : 'bg-zinc-400'}`} />
                      {a.is_online ? 'Online' : 'Offline'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-600">{a.country || 'Unknown'}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-zinc-600">
                      {a.device_type === 'Mobile' ? <Smartphone className="w-3 h-3" /> : a.device_type === 'Tablet' ? <Tablet className="w-3 h-3" /> : <Monitor className="w-3 h-3" />}
                      {a.device_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-600">{a.os || 'Unknown'}</td>
                  <td className="px-4 py-3 text-zinc-600">{a.browser || 'Unknown'}</td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{a.last_active ? formatTimeAgo(a.last_active) : '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-100">
          <p className="text-xs text-zinc-400">Page {page} of {pages}</p>
          <div className="flex gap-1">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              className="p-1.5 rounded-lg border border-zinc-200 hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="activity-prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= pages}
              className="p-1.5 rounded-lg border border-zinc-200 hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="activity-next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CountriesTable({ countries, loading }) {
  if (loading) return <div className="bg-white rounded-xl border border-zinc-200 p-6 animate-pulse"><div className="h-5 bg-zinc-100 rounded w-40 mb-4" /><div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-4 bg-zinc-100 rounded" />)}</div></div>;

  return (
    <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden" data-testid="countries-table-section">
      <div className="p-4 border-b border-zinc-100">
        <h3 className="font-bold text-zinc-900 flex items-center gap-2">
          <Globe className="w-4 h-4 text-[#002FA7]" /> Countries
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="countries-table">
          <thead>
            <tr className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wide">
              <th className="text-left px-4 py-3 font-medium">Country</th>
              <th className="text-right px-4 py-3 font-medium">Total Users</th>
              <th className="text-right px-4 py-3 font-medium">Online Now</th>
            </tr>
          </thead>
          <tbody>
            {countries.length === 0 ? (
              <tr><td colSpan={3} className="px-4 py-8 text-center text-zinc-400 text-sm">No country data yet</td></tr>
            ) : (
              countries.map((c, i) => (
                <tr key={c.country} className="border-t border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`country-row-${i}`}>
                  <td className="px-4 py-3 font-medium text-zinc-900">{c.country}</td>
                  <td className="px-4 py-3 text-right text-zinc-600">{c.total}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`text-sm font-medium ${c.online > 0 ? 'text-emerald-600' : 'text-zinc-400'}`}>{c.online}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DevicesSection({ devices, loading }) {
  if (loading) return <div className="bg-white rounded-xl border border-zinc-200 p-6 animate-pulse"><div className="h-5 bg-zinc-100 rounded w-40 mb-4" /><div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-4 bg-zinc-100 rounded" />)}</div></div>;

  if (!devices) return null;

  const maxDevice = Math.max(...(devices.devices?.map(d => d.count) || [1]));
  const maxOs = Math.max(...(devices.os?.map(d => d.count) || [1]));
  const maxBrowser = Math.max(...(devices.browsers?.map(d => d.count) || [1]));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="devices-section">
      {/* Device Types */}
      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
        <div className="p-4 border-b border-zinc-100">
          <h3 className="font-bold text-zinc-900 flex items-center gap-2 text-sm">
            <Monitor className="w-4 h-4 text-[#002FA7]" /> Device Types
          </h3>
        </div>
        <div className="p-4 space-y-3">
          {(devices.devices || []).length === 0 ? (
            <p className="text-xs text-zinc-400 text-center py-4">No data</p>
          ) : (
            devices.devices.map(d => (
              <div key={d.name} data-testid={`device-type-${d.name.toLowerCase()}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-zinc-700 flex items-center gap-1.5">
                    {d.name === 'Mobile' ? <Smartphone className="w-3 h-3" /> : d.name === 'Tablet' ? <Tablet className="w-3 h-3" /> : <Monitor className="w-3 h-3" />}
                    {d.name}
                  </span>
                  <span className="text-xs font-semibold text-zinc-900">{d.count}</span>
                </div>
                <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#002FA7] rounded-full transition-all" style={{ width: `${(d.count / maxDevice) * 100}%` }} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* OS */}
      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
        <div className="p-4 border-b border-zinc-100">
          <h3 className="font-bold text-zinc-900 flex items-center gap-2 text-sm">
            <Monitor className="w-4 h-4 text-emerald-600" /> Operating Systems
          </h3>
        </div>
        <div className="p-4 space-y-3">
          {(devices.os || []).length === 0 ? (
            <p className="text-xs text-zinc-400 text-center py-4">No data</p>
          ) : (
            devices.os.map(d => (
              <div key={d.name} data-testid={`device-os-${d.name.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-zinc-700">{d.name}</span>
                  <span className="text-xs font-semibold text-zinc-900">{d.count}</span>
                </div>
                <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${(d.count / maxOs) * 100}%` }} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Browsers */}
      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
        <div className="p-4 border-b border-zinc-100">
          <h3 className="font-bold text-zinc-900 flex items-center gap-2 text-sm">
            <Globe className="w-4 h-4 text-amber-600" /> Browsers
          </h3>
        </div>
        <div className="p-4 space-y-3">
          {(devices.browsers || []).length === 0 ? (
            <p className="text-xs text-zinc-400 text-center py-4">No data</p>
          ) : (
            devices.browsers.map(d => (
              <div key={d.name} data-testid={`device-browser-${d.name.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-zinc-700">{d.name}</span>
                  <span className="text-xs font-semibold text-zinc-900">{d.count}</span>
                </div>
                <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${(d.count / maxBrowser) * 100}%` }} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function formatTimeAgo(isoStr) {
  const d = new Date(isoStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AdminAnalytics() {
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [actTotal, setActTotal] = useState(0);
  const [actPage, setActPage] = useState(1);
  const [actPages, setActPages] = useState(1);
  const [actLoading, setActLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [devices, setDevices] = useState(null);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchOverview = useCallback(async () => {
    try {
      const { data } = await analyticsAPI.getOverview();
      setOverview(data);
    } catch (e) { console.error('overview fetch error', e); }
    finally { setOverviewLoading(false); }
  }, []);

  const fetchActivity = useCallback(async (pg = 1, f = {}) => {
    setActLoading(true);
    try {
      const { data } = await analyticsAPI.getActivity({ page: pg, limit: 25, ...f });
      setActivities(data.activities || []);
      setActTotal(data.total || 0);
      setActPage(data.page || 1);
      setActPages(data.pages || 1);
    } catch (e) { console.error('activity fetch error', e); }
    finally { setActLoading(false); }
  }, []);

  const fetchCountries = useCallback(async () => {
    try {
      const { data } = await analyticsAPI.getCountries();
      setCountries(Array.isArray(data) ? data : []);
    } catch (e) { console.error('countries fetch error', e); }
    finally { setCountriesLoading(false); }
  }, []);

  const fetchDevices = useCallback(async () => {
    try {
      const { data } = await analyticsAPI.getDevices();
      setDevices(data);
    } catch (e) { console.error('devices fetch error', e); }
    finally { setDevicesLoading(false); }
  }, []);

  useEffect(() => {
    fetchOverview();
    fetchActivity(1, filters);
    fetchCountries();
    fetchDevices();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchActivity(1, filters);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    setOverviewLoading(true);
    setCountriesLoading(true);
    setDevicesLoading(true);
    await Promise.all([
      fetchOverview(),
      fetchActivity(actPage, filters),
      fetchCountries(),
      fetchDevices(),
    ]);
    setRefreshing(false);
  };

  return (
    <div className="space-y-6 max-w-7xl" data-testid="admin-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-zinc-900 tracking-tight">User Analytics</h1>
          <p className="text-sm text-zinc-500 mt-1">Real-time user activity, geolocation & device tracking</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#002FA7] rounded-lg hover:bg-[#0025 85] transition-colors disabled:opacity-50"
          data-testid="analytics-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Overview Cards */}
      <OverviewSection overview={overview} loading={overviewLoading} />

      {/* Activity Table */}
      <ActivityTable
        activities={activities}
        total={actTotal}
        page={actPage}
        pages={actPages}
        onPageChange={(pg) => { setActPage(pg); fetchActivity(pg, filters); }}
        loading={actLoading}
        filters={filters}
        onFilterChange={setFilters}
      />

      {/* Bottom Grid: Countries + Devices */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CountriesTable countries={countries} loading={countriesLoading} />
        <div /> {/* Spacer for grid alignment */}
      </div>

      {/* Device Breakdown */}
      <DevicesSection devices={devices} loading={devicesLoading} />
    </div>
  );
}
